// @flow
import React from 'react'
import i18n from 'live/services/i18n'
import { darken } from 'polished'
import { ControlButton } from 'live/components/ui/CardContainer'
import styled, { css } from 'styled-components'
import { extractJson } from 'plugin-liverig/shared/asset/lib/utils'
import globalNotification from 'live/services/global-notification'
import { rawBlocksFromText } from 'live/components/forms/ui/RichText'
import { getReportFromSitop } from './api'
import { DropdownMenuItem } from 'plugin-liverig/shared/asset/styled'
import dateService from 'live/services/date'

export function DailyReportSitopControl({ intervention, confirmFill, setLoading, fillEmptyValues, timestamp }) {
    const [sitopDate, setSitopDate] = React.useState(null)
    const [sitopTime, setSitopTime] = React.useState(null)

    const getSitopReport = React.useCallback(
        intervention ? () => getReportFromSitop(intervention).then(extractJson) : null,
        [intervention]
    )

    React.useEffect(() => {
        getSitopReport &&
            getSitopReport().then(({ data: sitopData }: { data: SitopData }) => {
                setSitopTime(sitopData.Hora)
                setSitopDate(sitopData.Data)
            }).catch(e => {})
    }, [getSitopReport])

    const loadFromSitop = React.useCallback(() => {
        setLoading(true)

        getSitopReport()
            .then(({ data: sitopData }: { data: SitopData }) => {
               
                fillEmptyValues(sitopDataToReport(sitopData, intervention))    
            })
            .catch(e => {
                console.error(e)
                setLoading(false)
                globalNotification.info(i18n('Error while fetching SITOP data'))
            })
    }, [setLoading, getSitopReport, fillEmptyValues])

    const timestampIsToday = dateService.formatDate(timestamp) === dateService.formatDate(+new Date())

    return (
        <React.Fragment>
            <StyledDropdownMenuItem key={'Load SITOP'} tabIndex={0} disabled={!timestampIsToday}>
                <StyledControlButton
                    disabled={!timestampIsToday || !sitopDate || !sitopTime}
                    onClick={loadFromSitop}
                    className="ml-6"
                >
                    <div>
                        <div className="sitopButton">
                            <i className="far fa-arrow-from-top mr-10" />
                            <span>{i18n('Latest from SITOP')}</span>
                        </div>
                        <div>
                            {timestampIsToday && sitopDate && sitopTime && intervention ? (
                                <div className="latestSitopDate">
                                    <span className="itemDescription">
                                        {sitopDate}: {sitopTime}
                                    </span>
                                </div>
                            ) : (
                                <div className="latestSitopDate">
                                    <div>
                                        <span className="itemDescription">
                                            {intervention
                                                ? i18n("There's no SITOP data for the")
                                                : i18n('Please choose an intervention')}
                                        </span>
                                    </div>
                                    {intervention && (
                                        <div className="bottomDescription">
                                            <span className="itemDescription">{i18n('selected day')}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </StyledControlButton>
            </StyledDropdownMenuItem>
        </React.Fragment>
    )
}

function sitopDataToReport(sitopData: SitopData, intervention): ReportData {
    return {
        intervention: intervention,
        bulletinNumber: sitopData.NumeroDoBoletim || '',
        currentSituation: rawBlocksFromText(sitopData.SituacaoAtual || ''),
        responsible: sitopData.CoordenadorResponsavel || '',
        responsibleContact: sitopData.ContatoCoordenadorResponsavel || '',
        supervisors: rawBlocksFromText(fiscaisToText(sitopData.Fiscais) || ''),
        rigContacts: rawBlocksFromText(sitopData.ContatosDaSonda || ''),
        // currentHoleDiameter: sitopData missing,
        currentDepth: sitopData.ProfundidadeAtual || '',
        lastShoe: sitopData.UltimaSapata || '',
        lot: sitopData.lot || '',
        // inclination: sitopData missing
        // fluidType: sitopData missing,
        // fluidWeight: sitopData missing,
        // riskAssessment: sitopData missing,
        // incidentReport: sitopData missing,
        // lastDaySummary: sitopData missing,
        lastShiftSummary: rawBlocksFromText(sitopData.Resumo || ''),
        nextOperations: rawBlocksFromText(sitopData.ProximasOperacoes || '')
        // csd: sitopData.Responsavel || ''
        // csdContacts: sitopData missing,
    }
}

function fiscaisToText(fiscais: $PropertyType<SitopData, 'Fiscais'>) {
    return _.map(fiscais, (fiscal, sign) => `${fiscal} (${sign})`).join(', ')
}

const StyledControlButton = styled(ControlButton)`
    font-size: 14px;
    opacity: 1;

    &:hover {
        color: ${({ theme }) => darken(0.1, theme['link-button'])};
    }

    .sitopButton {
        i {
            font-size: 16px;
            position: relative;
            top: 4px;
        }
        ${({ disabled }) =>
            disabled &&
            css`
                opacity: 0.35;
            `}
    }
`

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
    font-size: 14px;
    color: ${({ theme }) => theme['brand-primary']};
    display: flex;
    margin-top: 10px;
    padding: 6px 15px;
    font-weight: 600;
    font-family: ${({ theme }) => theme['font-semibold']};

    > span {
        font-size: 16px;
    }

    &:hover {
        background-color: ${({ theme }) => theme['gray-lighter']};
    }

    > span {
        flex-shrink: 0;
        flex-basis: 22px;
        text-align: center;
    }

    > div {
        flex: 1;
    }

    &:not(:first-child) {
        margin-top: 0px;
    }

    .itemDescription {
        font-size: 11px;
        font-family: ${({ theme }) => theme['font-regular']};
        color: ${({ theme }) => theme['dark-icons']};
        font-weight: 1;
        margin-top: -4px;
    }
    .bottomDescription {
        margin-top: -4px;
        opacity: 1;
    }
    .sitopControl {
        margin-left: -5px;
    }
    .latestSitopDate {
        margin-left: 22px;
        opacity: 1;
    }
`

type SitopData = {
    RequestDate: string, // '17/06/2019'
    RequestTime: string, // '1'
    RequestCode: string, // '629'
    PluginVersion: string, // '2.15.1-SNAPSHOT'
    Data: string, // '31/10/2018'
    Hora: string, // '15:58'
    Unidade: string, // 'NS-45'
    Poco: string, // '7-LL-83D-RJS'
    Responsavel: string, // 'POCOS/CAMAP-AUP/POLO-SAN/CAMP-I'
    NumeroEsperadoFiscais: number,
    PrevisaoOriginalDataMaisTarde: string, // '17/11/2016'
    PrevisaoTerminoIntervencaoMaisCedo: string, // '10/11/2016'
    PrevisaoTerminoIntervencaoMaisTarde: string, // '16/11/2016'
    lot: string, // FIT 12,5 lb/gal @ sapata 14"
    projetoBloco: string, // BUZIUS
    ObjetivoIntervencao: string, // 'Perfuração do 7-LL-83D-RJS.'
    ProximaIntervencao: string, // 'Completação do 7-LL-83D-RJS.'
    UltimoTestePressaoBOP: string, // '15/10/2016.'
    UltimoTesteFuncionalBOP: string, // '15/10/2016.'
    UltimoTesteDeteccaoKick: string, // '16/10/2016.'
    UltimoSimuladoKick: string, // 'Perfurando (transferência de volume); 19/10/16; Turma B; Volume transferido: 10 bbl. Volume detectado pela sonda: 5 bbl. Volume detectado pelo mud logging: 5 bbl. Tempo de reação: 1 min. Resultado: Positivo.'
    Resumo: string, // 'Operações Principais - MP:\nRetirada coluna com BHA 17 1/2" de 4978 m a 3468 m.\n\nObs1: Perda de 4446 bbl em 12 horas. Taxa média de 370,5 bph.\nObs2: Efetuados dois surveys de investigação a 3792 m. Temperatura de fundo (Ontrak) em ambos os surveys: 104,1° F\n\nOperações Paralelas - MA:\n Montadas 11/11 seções de 13 3/8"\n Montadas 36/60 seções de revestimento 13 5/8"'
    SituacaoAtual: string, // 'Operação Principal - MP:\nEfetuando 3ª survey a 3468 m (93,2 °F no Ontrak).\n\nOperação Paralela - MA:\n Montando seções de revestimento 13 5/8".'
    ProximasOperacoes: string, // 'Operações Principais:\nRetirar coluna até a sapata (3138 m).\nFechar BOP, deslocar olefina pela linha de kill e determinar pressão do nível estático\n\nOperações Paralelas:\nMontar seções do revestimento intermediário (em andamento).\nMontar serão com PADPRT.\nMontar serão com cabeça de cimentação.\nQuebrar o BHA 17 °".\nMontar o BHA 12 °".'
    AssinaturasFiscais: string, // 'Saldanha (L) / Alexandre Folz (D) / Davi Vale (N) / Leandro (AF).'
    UltimaSapata: string, // '22" a 3138 m.'
    ChaveCoordenadorResponsavel: string, // 'PS1G5'
    ContatoCoordenadorResponsavel: string, // 'Fones: Ramal: 759-2371; Externo: (13)3249-2371; Celular: (13)996-210-285'
    NumeroDoBoletim: string, // '17'
    ContatosDaSonda: string, // 'Sala de Rádio: 863-9520 / (22) 2753-9520 / (22) 3321-0794/0795 (Externo QGOG)\nSala do Fiscal: 863-9522 / (22) 2753-9522\nApoio à Fiscalização: 863-9720 / (22) 2753-9720\nSala do Químico: 863-9524 / (22) 2753-9524\nINMARSAT: 00 870-773-928-854/ 00 870-773-928-857\nChave de bandeja do fiscal: BNS45; E-mail do fiscal: sondans45@petrobras.com.br'
    CoordenadorResponsavel: string, // 'Eduardo Figueiredo'
    ProfundidadeAtual: string, // '4988 m/4943 m.'
    ProfundidadeUltimaSapata: string, // '3138 m'
    ProximoTestePressaoBOP: string, // '05/11/2016'
    ProximoTesteFuncionalBOP: string, // '05/11/2016'
    ProximoTesteDeteccaoKick: string, // '06/11/2016'
    ProximoSimuladoKick: string, // '26/10/2016'
    DaysUntilProximoTestePressaoBOP: number, // -954
    DaysUntilProximoTesteFuncionalBOP: number, // -954
    DaysUntilProximoTesteDeteccaoKick: number, // -953
    DaysUntilProximoSimuladoKick: number, // -964
    TipoIntervencaoAtual: string, // 'Perfuração'
    TipoIntervencaoProxima: string, // 'Completação'
    PocoIntervencaoAtual: string, // '7-LL-83D-RJS'
    PocoIntervencaoProxima: string, // '7-LL-83D-RJS'
    AssinaturaFiscais: Array<string>, // ['Saldanha (L)', 'Alexandre Folz (D)', 'Davi Vale (N)', 'Leandro (AF)']
    Fiscais: { [string]: string[] }, // { L: ['Saldanha'], D: ['Alexandre Folz'], N: ['Davi Vale'], AF: ['Leandro'] }
    timestamp: number, // 1561042056730
    __type: string, // 'sitop'
    __src: string // 'replay/sitop'
}
